/**
 * IndexedDB-backed persistent cache for API responses.
 *
 * Used as a local-first fallback so the configurator stays functional
 * even when the remote API (api/v1/years, api/v1/check) is unavailable.
 *
 * Strategy:
 *  1. Attempt the live network request.
 *  2. On success → write result to IDB, return result.
 *  3. On failure → read from IDB (ignoring TTL) so stale data beats no data.
 *  4. If IDB is also empty → re-throw the original error.
 */

const DB_NAME = "scs-configurator-cache";
const DB_VERSION = 3; // bumped to add vehicle + vehicle_addons stores

const STORES = {
  YEARS:               "years",
  CHECK:               "check",
  MAKES:               "makes",
  MODELS:              "models",
  VEHICLE_AVAILABILITY: "vehicle_availability",
  VEHICLE:             "vehicle",
  VEHICLE_ADDONS:      "vehicle_addons",
} as const;

/** How long a cached entry is considered "fresh" (won't bypass a network call). */
const TTL_MS = {
  YEARS:               7 * 24 * 60 * 60 * 1000, // 7 days  – vehicle list changes rarely
  CHECK:               24 * 60 * 60 * 1000,      // 24 hours – compatibility data is stable
  MAKES:               7 * 24 * 60 * 60 * 1000, // 7 days
  MODELS:              7 * 24 * 60 * 60 * 1000, // 7 days
  VEHICLE_AVAILABILITY: 7 * 24 * 60 * 60 * 1000, // 7 days – configurator questions are stable
  VEHICLE:             30 * 24 * 60 * 60 * 1000, // 30 days – slug→vehicle mapping is static
  VEHICLE_ADDONS:      7 * 24 * 60 * 60 * 1000,  // 7 days  – add-on inventory changes infrequently
};

interface CacheEntry {
  key: string;
  data: unknown;
  cachedAt: number;
  expiresAt: number;
}

// ── DB bootstrap ──────────────────────────────────────────────────────────────

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise !== null) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      for (const storeName of Object.values(STORES)) {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: "key" });
        }
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      console.warn("[IDBCache] Failed to open IndexedDB:", req.error);
      reject(req.error);
    };
  });

  return dbPromise;
}

// ── Primitive helpers ─────────────────────────────────────────────────────────

async function idbGet(store: string, key: string): Promise<CacheEntry | undefined> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(store, "readonly");
      const req = tx.objectStore(store).get(key);
      req.onsuccess = () => resolve(req.result as CacheEntry | undefined);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn("[IDBCache] idbGet error:", err);
    return undefined;
  }
}

async function idbSet(store: string, entry: CacheEntry): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(store, "readwrite");
      const req = tx.objectStore(store).put(entry);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn("[IDBCache] idbSet error:", err);
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Wraps a network `fetcher` with an IDB fallback.
 *
 * @param store   - IDB object-store name (use STORES.YEARS / STORES.CHECK)
 * @param key     - Unique string key for this cached value
 * @param ttlMs   - How long until the cached value is considered stale
 * @param fetcher - Async function that performs the real network call
 * @returns The freshest data available (network > fresh IDB > stale IDB)
 */
export async function withIDBFallback<T>(
  store: string,
  key: string,
  ttlMs: number,
  fetcher: () => Promise<T>
): Promise<T> {
  // 1. Try to serve a still-fresh IDB entry to save a round-trip
  try {
    const cached = await idbGet(store, key);
    if (cached && cached.expiresAt > Date.now()) {
      // Return in background; fire network fetch to keep cache warm
      fetcher()
        .then((fresh) => {
          idbSet(store, { key, data: fresh, cachedAt: Date.now(), expiresAt: Date.now() + ttlMs });
        })
        .catch(() => {/* network failed – stale IDB will handle next time */});
      return cached.data as T;
    }
  } catch {
    // IDB unavailable – fall through to network
  }

  // 2. Attempt network request
  try {
    const fresh = await fetcher();
    // Persist to IDB so future failures can fall back
    idbSet(store, { key, data: fresh, cachedAt: Date.now(), expiresAt: Date.now() + ttlMs });
    return fresh;
  } catch (networkError) {
    console.warn("[IDBCache] Network request failed, attempting IDB fallback:", networkError);

    // 3. Network failed → return stale IDB data (any age)
    try {
      const stale = await idbGet(store, key);
      if (stale?.data !== undefined) {
        console.info("[IDBCache] Serving stale cached data for key:", key);
        return stale.data as T;
      }
    } catch {
      // IDB unavailable too – nothing we can do
    }

    // 4. Nothing in IDB either – re-throw so callers can show an error state
    throw networkError;
  }
}

export const IDB_STORES = STORES;
export const IDB_TTL = TTL_MS;

// ── Seed loader ───────────────────────────────────────────────────────────────

/**
 * Shape of the generated idb-seed.json file.
 * Each store maps cache-keys → raw API response data.
 */
export interface IDBSeedFile {
  /** ISO-8601 timestamp written by generate-idb-seed.js */
  generatedAt: string;
  /** Bump this when the seed schema changes to force a reload */
  version: number;
  /**
   * Unique shard identifier written by the generator
   * (e.g. "years", "makes", "models-2025").
   * Used as the localStorage version-tracking key so each split file
   * is seeded independently.
   */
  key?: string;
  stores: {
    [storeName: string]: {
      [key: string]: unknown;
    };
  };
}

const SEED_VERSION_PREFIX = "scs-idb-seed";
/** Legacy single-file key — kept for backwards compat during rollover */
const SEED_VERSION_KEY_LEGACY = "scs-idb-seed-version";

/**
 * Populate IndexedDB from a bundled static seed file on cold start.
 *
 * Rules:
 *  - Runs once per `seed.generatedAt` timestamp (tracked in localStorage).
 *  - Only writes a key if IDB does not already have an entry for it —
 *    live network data always takes precedence.
 *  - Sets expiresAt = now + TTL so the background-refresh path in
 *    withIDBFallback will still fire after the TTL window.
 */
export async function seedIDBFromStaticData(seed: IDBSeedFile): Promise<void> {
  try {
    // Per-shard version key: "scs-idb-seed:years", "scs-idb-seed:models-2025", etc.
    // Falls back to the legacy single-file key for the monolithic seed format.
    const versionKey = seed.key
      ? `${SEED_VERSION_PREFIX}:${seed.key}`
      : SEED_VERSION_KEY_LEGACY;

    const stored = localStorage.getItem(versionKey);
    if (stored === seed.generatedAt) return; // Already loaded this shard at this version

    const ttlByStore: Record<string, number> = {
      [STORES.YEARS]:               TTL_MS.YEARS,
      [STORES.MAKES]:               TTL_MS.MAKES,
      [STORES.MODELS]:              TTL_MS.MODELS,
      [STORES.CHECK]:               TTL_MS.CHECK,
      [STORES.VEHICLE_AVAILABILITY]: TTL_MS.VEHICLE_AVAILABILITY,
    };
    const now = Date.now();

    for (const [storeName, entries] of Object.entries(seed.stores)) {
      const ttl = ttlByStore[storeName] ?? TTL_MS.YEARS;
      for (const [key, data] of Object.entries(entries)) {
        const existing = await idbGet(storeName, key);
        if (!existing) {
          await idbSet(storeName, {
            key,
            data,
            cachedAt: new Date(seed.generatedAt).getTime(),
            expiresAt: now + ttl,
          });
        }
      }
    }

    localStorage.setItem(versionKey, seed.generatedAt);
  } catch (err) {
    // Seed loading is best-effort; never block app startup
    console.warn("[IDBCache] Seed load failed:", err);
  }
}
